import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'

export interface EmployeeDocument {
  id: string
  documentType: 'employment_contract' | 'instructor_licence' | 'drivers_licence' | 'other'
  title: string
  status: 'pending_upload' | 'active'
  createdAt: string
}

const DOCS_BUCKET = 'employee-docs'

/**
 * Fetch + upload for one employee's documents (migration 085) — same two-phase
 * draft/upload/finalize shape as useSchoolProfile.ts's document handling, scoped by
 * school_membership_id instead of school_id. Used by EmployeeDetail.tsx for both the
 * school admin's view (upload + read) and the employee's own Settings view (read only,
 * via the RLS "employee_documents: own read" policy).
 */
export function useEmployeeDocuments(schoolMembershipId: string | null) {
  const [documents, setDocuments] = useState<EmployeeDocument[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)

  const fetchAll = useCallback(async () => {
    if (!schoolMembershipId) { setDocuments([]); setLoading(false); return }
    setLoading(true)
    setError(null)
    try {
      const { data, error: fetchErr } = await supabase
        .from('employee_documents')
        .select('id, document_type, title, status, created_at')
        .eq('school_membership_id', schoolMembershipId)
        .eq('status', 'active')
        .order('created_at', { ascending: false })

      if (fetchErr) { setError('Could not load documents.'); return }
      setDocuments((data ?? []).map(d => ({
        id: d.id,
        documentType: d.document_type,
        title: d.title,
        status: d.status,
        createdAt: d.created_at,
      })))
    } finally {
      setLoading(false)
    }
  }, [schoolMembershipId])

  useEffect(() => { fetchAll() }, [fetchAll])

  const uploadDocument = useCallback(async (
    file: File,
    documentType: 'employment_contract' | 'instructor_licence' | 'drivers_licence' | 'other',
    title: string,
  ): Promise<{ ok: boolean; code?: string }> => {
    if (!schoolMembershipId || uploading) return { ok: false, code: 'busy' }
    setUploading(true)
    try {
      const { data: draft, error: draftError } = await supabase.rpc('create_employee_document_draft', {
        p_school_membership_id: schoolMembershipId,
        p_document_type: documentType,
        p_title: title,
      })
      if (draftError || !draft) return { ok: false, code: 'internal_error' }
      const { document_id, storage_path } = (Array.isArray(draft) ? draft[0] : draft) as { document_id: string; storage_path: string }

      const { error: upErr } = await supabase.storage.from(DOCS_BUCKET).upload(storage_path, file, { contentType: file.type, upsert: false })
      if (upErr) return { ok: false, code: 'upload_failed' }

      const { error: finalizeError } = await supabase.rpc('finalize_employee_document', { p_document_id: document_id })
      if (finalizeError) return { ok: false, code: 'finalize_failed' }

      await fetchAll()
      return { ok: true }
    } finally {
      setUploading(false)
    }
  }, [schoolMembershipId, uploading, fetchAll])

  const documentUrl = useCallback(async (documentId: string): Promise<string | null> => {
    const { data, error: fnError } = await supabase.functions.invoke('get-employee-document-url', { body: { documentId } })
    if (fnError || !data?.url) return null
    return data.url as string
  }, [])

  return { documents, loading, error, uploading, uploadDocument, documentUrl, refresh: fetchAll }
}
